import type { ReactNode } from "react";
import type { Software } from "@/types";
import { formatDownloads, platformLabel } from "@/utils/software";
import RatingStars from "@/components/base/RatingStars";

export default function SoftwareHeader({ software, topSlot }: { software: Software; topSlot?: ReactNode }) {
  const meta = [
    { label: "当前版本", value: `v${software.version}`, icon: "ri-price-tag-3-line" },
    { label: "更新时间", value: software.updatedAt, icon: "ri-calendar-line" },
    { label: "软件大小", value: software.size, icon: "ri-database-2-line" },
    { label: "开发者", value: software.developer, icon: "ri-user-star-line" },
  ];

  return (
    <section className="bg-background-100 border-b border-background-200">
      <div className="w-full max-w-[1240px] mx-auto px-4 md:px-6 pt-6 pb-8">
        {topSlot && <div className="mb-5">{topSlot}</div>}
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-background-50 border border-background-200 shrink-0">
            <img
              src={software.icon}
              alt={`${software.name} 软件图标`}
              title={`${software.name} 最新版下载`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">{software.name}</h1>
              <span className="text-xs text-primary-700 bg-primary-100 rounded-full px-2.5 py-1">v{software.version}</span>
              {software.price === "free" ? (
                <span className="text-xs text-accent-700 bg-accent-100 rounded-full px-2.5 py-1">免费</span>
              ) : (
                <span className="text-xs text-secondary-700 bg-secondary-200 rounded-full px-2.5 py-1">付费 ¥{software.priceValue}</span>
              )}
            </div>

            <p className="mt-2.5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">{software.shortDesc}</p>

            <div className="mt-3.5 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs md:text-sm text-secondary-500">
              <RatingStars value={software.rating} size="md" />
              <span className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-download-2-line" aria-hidden="true"></i></span>
                {formatDownloads(software.downloads)}次下载
              </span>
              <div className="flex items-center gap-1.5">
                {software.platforms.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 text-secondary-600 bg-background-50 border border-background-200 rounded-md px-2 py-0.5">
                    <span className="flex items-center justify-center w-3.5 h-3.5">
                      <i className={p === "ios" ? "ri-apple-fill" : "ri-android-line"} aria-hidden="true"></i>
                    </span>
                    {platformLabel(p)}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={software.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-7 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-download-cloud-line text-lg" aria-hidden="true"></i></span>
                立即下载最新版
              </a>
              <span className="text-xs text-secondary-400 flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-information-line" aria-hidden="true"></i></span>
                下载仅为跳转演示，请前往应用商店或官方网站获取正版
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {meta.map((m) => (
            <div key={m.label} className="bg-background-50 rounded-lg border border-background-200 p-4">
              <p className="text-xs text-secondary-500 flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4"><i className={m.icon} aria-hidden="true"></i></span>
                {m.label}
              </p>
              <p className="mt-1.5 text-sm font-medium text-foreground-900 truncate">{m.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}